import filter from 'lodash/filter'
import find from 'lodash/find'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import reduce from 'lodash/reduce'
import reject from 'lodash/reject'
import size from 'lodash/size'
import sortBy from 'lodash/sortBy'

import {createSelector} from 'reselect'

import {SUIT_TUX_GROUP, TRANSACTION_TYPE_BUY, TRANSACTION_TYPE_RENT} from '../const'
import {not} from '../utils'
import {CategoryOrder} from './enumTS'
import {hasLength} from './helpers'

// Get

// Holds all looks, hto and non-hto, visible to the user
export const looks = (state) => get(state, 'looks')

// Details By Look

export const getLookProducts = (look) => get(look, 'products')

// Args

export const getLookById = (state, id) => find(looks(state), {id})
export const lookProductsByLookId = (state, id) => getLookProducts(getLookById(state, id))

/**
 * Retrieve the suit or tuxedo product from a specific look. If there is not one, return
 * an empty object.
 *
 * @param {Object} state The Redux state object
 * @param {String} id The Look ID
 * @return {Object} A suit or tux product object if it exists, otherwise an empty object
 */
export const suitOrTuxProductByLookId = (state, id) =>
  lookProductsByLookId(state, id).find(({category}) => SUIT_TUX_GROUP.includes(category)) || {}

// Computed

export const getLookObjectIndex = (lookId, initLooks) => {
  let $looks = initLooks
  if (typeof $looks === 'undefined') $looks = TBT.store.getState().looks
  for (let index = 0; index < $looks.length; index += 1) {
    if ($looks[index].id === lookId) {
      return index
    }
  }
  return -1
}

export const getLookTotal = createSelector(lookProductsByLookId, ($products) =>
  reduce(
    $products,
    (prev, cur) => {
      if (cur.transactionType === TRANSACTION_TYPE_RENT) {
        return prev + cur.rentPrice
      }
      return prev + cur.buyPrice
    },
    0,
  ),
)

export const getLookObject = (lookId, initLooks) => {
  let $looks = initLooks
  if (typeof $looks === 'undefined') $looks = TBT.store.getState().looks
  const index = getLookObjectIndex(lookId, $looks)
  return index >= 0 ? $looks[index] : null
}

export const getLookName = (lookId, $looks) => {
  const look = getLookObject(lookId, $looks)
  return look === null ? '' : look.name
}

export const getHtoLooks = createSelector(looks, ($looks) => filter($looks, {hto: true}))

export const getNonHtoLooks = createSelector(looks, ($looks) => reject($looks, {hto: true}))

export const getFirstNonHtoLook = createSelector(getNonHtoLooks, ($looks) => $looks[0])

export const getMostRecentlyAddedNonHtoLook = createSelector(
  getNonHtoLooks,
  (nonHtoLooks) => nonHtoLooks[nonHtoLooks.length - 1],
)

// NOTE: This selector is used to display products in the dashboard in the order that
//  the category placeholders appear.
export const getOrderedNonHtoLooks = createSelector(getNonHtoLooks, ($looks) =>
  map($looks, (look) => ({
    ...look,
    ...{
      products: sortBy(look.products, ({category}) => CategoryOrder[category]),
    },
  })),
)

export const totalNumberOfLooks = createSelector(looks, size)

export const hasLooks = createSelector(looks, hasLength)

export const hasNoLooks = createSelector(hasLooks, not)

export const hasHtoLooks = createSelector(getHtoLooks, hasLength)

export const hasNonHtoLooks = createSelector(getNonHtoLooks, hasLength)

export const hasOneRentalLook = createSelector(getNonHtoLooks, ($looks) => size($looks) === 1)

export const hasItemsOfTransactionType = (state, transactionType) => {
  const $looks = getNonHtoLooks(state)
  return $looks.some((look) =>
    getLookProducts(look).some((product) => product.transactionType === transactionType),
  )
}

export const hasPurchaseItems = (state) => hasItemsOfTransactionType(state, TRANSACTION_TYPE_BUY)

export const hasRentalItems = (state) => hasItemsOfTransactionType(state, TRANSACTION_TYPE_RENT)

export const getOrderTypesInLooks = createSelector(
  hasHtoLooks,
  hasRentalItems,
  hasPurchaseItems,
  (hasHto, hasRentals, hasPurchases) => {
    const orderTypes = []
    if (hasHto) {
      orderTypes.push('hto')
    }
    if (hasRentals) {
      orderTypes.push('rental')
    }
    if (hasPurchases) {
      orderTypes.push('purchase')
    }
    return orderTypes
  },
)

export const allUserLookNames = (state) => {
  const userLooks = state.people.user
  const userLooksLength = userLooks.length
  const lookNames = []
  for (let i = 0; i < userLooksLength; i += 1) {
    lookNames.push(getLookName(userLooks[i], state.looks))
  }
  return lookNames
}

/**
 * Reduces each product in a specific look to its price based on the transaction
 * type of the product
 * @return {Number[]} An array of prices
 */
export const productPricesByTransactionType = createSelector(lookProductsByLookId, (products) =>
  products.map(({buyPrice, rentPrice, transactionType}) => {
    if (transactionType === TRANSACTION_TYPE_RENT) {
      return rentPrice
    }
    if (transactionType === TRANSACTION_TYPE_BUY) {
      return buyPrice
    }
    return 0
  }),
)

/**
 * Sums the prices taken from `productPricesByTransactionType`
 * @return {Number} Total price of products in a look
 */
export const sumProductPriceTotal = createSelector(productPricesByTransactionType, (prices) =>
  prices.reduce((sum, price) => sum + parseInt(price, 10), 0),
)

export const sumProductPriceTotalWithDiscount = createSelector([lookProductsByLookId], (products) =>
  products.reduce((sum, product) => {
    let price = 0
    if (product.transactionType === TRANSACTION_TYPE_RENT) {
      price = product.rentPrice
      price = Math.ceil(price * 0.8)
    } else if (product.transactionType === TRANSACTION_TYPE_BUY) {
      price = product.buyPrice
      // Apply 25% discount buy items
      // if (SUIT_TUX_GROUP.includes(product.category)) {
      price = Math.ceil(price * 0.8)
      // }
    }
    return sum + parseInt(price, 10)
  }, 0),
)

/**
 * Detects whether every product in a look has a valid transaction type or not
 * @return {Boolean}
 */
export const lookProductsHaveValidTransactionTypes = createSelector(
  lookProductsByLookId,
  (products) =>
    products.every(({transactionType}) =>
      [TRANSACTION_TYPE_BUY, TRANSACTION_TYPE_RENT].includes(transactionType),
    ),
)

export const isHtoByLookId = createSelector(getHtoLooks, getLookById, (htoLooks, look) =>
  htoLooks.some((htoLook) => htoLook.id === look.id),
)

/**
 * Returns true if the given look id matches a look with at least one product
 * @return {Boolean}
 */
export const lookHasProductsById = createSelector(lookProductsByLookId, hasLength)

/**
 * Returns an array of all looks with an empty products array
 * @return {Array}
 */
export const getLooksMissingProducts = createSelector(looks, (allLooks) =>
  allLooks.filter((look) => {
    return isEmpty(getLookProducts(look))
  }),
)

/**
 * Returns true if ANY look has an empty products array
 * @return {Boolean}
 */
export const anyLookIsMissingProducts = createSelector(getLooksMissingProducts, hasLength)

/**
 * Loops through each look to drill down to the products
 * @return {Array} Returns a new array of all look products
 */
export const allLookProducts = createSelector(looks, ($looks) =>
  $looks.reduce((arr, {products}) => arr.concat(products), []),
)

/**
 * Gets the number of products in all looks
 * @return {Number} Returns the size of the allLookProducts array
 */
export const totalNumberOfProducts = createSelector(allLookProducts, (products) => products.length)
